// src/pages/PesquisaJurisprudencia/components/TagSelectionModal.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';

const TagSelectionModal = React.memo(({
  isOpen,
  onClose,
  tags,
  selectedTags,
  onTagsChange,
  loading
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [tempSelectedTags, setTempSelectedTags] = useState(selectedTags);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  useEffect(() => {
    if (isOpen) {
      setTempSelectedTags(selectedTags);
      setSearchTerm('');
    }
  }, [isOpen, selectedTags]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const filteredTags = useMemo(() => {
    if (!debouncedSearchTerm.trim()) return tags;
    const searchLower = debouncedSearchTerm.toLowerCase();
    return tags.filter(tagItem => tagItem.tag.toLowerCase().includes(searchLower));
  }, [tags, debouncedSearchTerm]);

  const handleTagToggle = useCallback((tag) => {
    setTempSelectedTags(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  }, []);

  const handleSelectAllFiltered = useCallback(() => {
    const filteredTagNames = filteredTags.map(t => t.tag);
    setTempSelectedTags(prev => {
      const next = [...prev];
      filteredTagNames.forEach(tag => { if (!next.includes(tag)) next.push(tag); });
      return next;
    });
  }, [filteredTags]);

  const handleClearAll = useCallback(() => setTempSelectedTags([]), []);

  const handleApply = useCallback(() => {
    onTagsChange(tempSelectedTags);
    onClose();
  }, [tempSelectedTags, onTagsChange, onClose]);

  const handleCancel = useCallback(() => {
    setTempSelectedTags(selectedTags);
    onClose();
  }, [selectedTags, onClose]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleCancel}>
      <div className="modal-tags-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-tags-header">
          <h3>Selecionar Tags ({tempSelectedTags.length} selecionadas)</h3>
          <button className="modal-close-btn" onClick={handleCancel}>✕</button>
        </div>

        <div className="modal-tags-search">
          <input
            type="text"
            placeholder="🔍 Buscar tags..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="modal-search-input"
          />
        </div>

        <div className="modal-tags-stats">
          Mostrando {filteredTags.length} de {tags.length} tags disponíveis
        </div>

        <div className="modal-tags-list">
          {loading ? (
            <div className="modal-loading">Carregando tags...</div>
          ) : filteredTags.length === 0 ? (
            <div className="modal-no-tags">
              {searchTerm ? 'Nenhuma tag encontrada' : 'Nenhuma tag disponível'}
            </div>
          ) : (
            filteredTags.map((tagItem, index) => (
              <div key={`${tagItem.tag}-${index}`} className="modal-tag-item">
                <input
                  type="checkbox"
                  id={`modal-tag-${tagItem.tag}-${index}`}
                  checked={tempSelectedTags.includes(tagItem.tag)}
                  onChange={() => handleTagToggle(tagItem.tag)}
                />
                <label htmlFor={`modal-tag-${tagItem.tag}-${index}`}>
                  {tagItem.tag}
                  <span className="modal-tag-count">({tagItem.quantidade})</span>
                </label>
              </div>
            ))
          )}
        </div>

        <div className="modal-tags-footer">
          <div className="modal-footer-left">
            <button className="btn-secondary" onClick={handleClearAll}>Limpar Tudo</button>
            <button className="btn-secondary" onClick={handleSelectAllFiltered}>
              Selecionar Filtradas ({filteredTags.length})
            </button>
          </div>
          <div className="modal-footer-right">
            <button className="btn-cancel" onClick={handleCancel}>Cancelar</button>
            <button className="btn-primary" onClick={handleApply}>
              Aplicar ({tempSelectedTags.length})
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});

export default TagSelectionModal;